import React from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceLine } from 'recharts';
import { BankAccount } from '../../types/buyerFinancialData';

interface LiquidityAnalysisChartProps {
  bankAccounts: BankAccount[];
  monthlyExpenses: number;
  className?: string;
}

export function LiquidityAnalysisChart({ bankAccounts, monthlyExpenses, className = '' }: LiquidityAnalysisChartProps) {
  // Calculate liquidity metrics
  const totalLiquidAssets = bankAccounts.reduce((sum, account) => sum + account.currentBalance, 0);
  const totalAverageBalance = bankAccounts.reduce((sum, account) => sum + account.averageBalance, 0);
  const totalMonthlyInflow = bankAccounts.reduce((sum, account) => sum + account.monthlyInflow, 0);
  const totalMonthlyOutflow = bankAccounts.reduce((sum, account) => sum + account.monthlyOutflow, 0);
  
  // Calculate liquidity ratios
  const currentLiquidityRatio = monthlyExpenses > 0 ? totalLiquidAssets / monthlyExpenses : 0;
  const averageLiquidityRatio = monthlyExpenses > 0 ? totalAverageBalance / monthlyExpenses : 0;
  
  // SBA requirements
  const sbaLiquidityRequirement = monthlyExpenses * 3; // 3 months of expenses
  const meetsSBARequirement = totalLiquidAssets >= sbaLiquidityRequirement;
  
  // Prepare data for chart
  const chartData = bankAccounts.map(account => ({
    account: `${account.bankName} ${account.accountNumber}`,
    currentBalance: account.currentBalance,
    averageBalance: account.averageBalance,
    monthlyInflow: account.monthlyInflow,
    monthlyOutflow: account.monthlyOutflow,
    netFlow: account.monthlyInflow - account.monthlyOutflow
  }));

  // Add SBA requirement line
  const chartDataWithRequirement = [
    ...chartData,
    {
      account: 'SBA Requirement',
      currentBalance: sbaLiquidityRequirement,
      averageBalance: sbaLiquidityRequirement,
      monthlyInflow: 0,
      monthlyOutflow: 0,
      netFlow: 0
    }
  ];

  // Custom tooltip
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      if (label === 'SBA Requirement') {
        return (
          <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
            <p className="font-medium text-gray-900 mb-2">{label}</p>
            <p className="text-sm text-gray-600">
              Minimum Required: ${data.currentBalance.toLocaleString()}
            </p>
            <p className="text-xs text-gray-500">
              (3 months of expenses)
            </p>
          </div>
        );
      }
      
      return (
        <div className="bg-white p-3 border border-gray-200 rounded-lg shadow-lg">
          <p className="font-medium text-gray-900 mb-2">{label}</p>
          <div className="space-y-1 text-sm">
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Current Balance:</span>
              <span className="font-semibold text-green-600">
                ${data.currentBalance.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Average Balance:</span>
              <span className="font-semibold text-blue-600">
                ${data.averageBalance.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Monthly Inflow:</span>
              <span className="font-semibold text-green-600">
                ${data.monthlyInflow.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4">
              <span className="text-gray-600">Monthly Outflow:</span>
              <span className="font-semibold text-red-600">
                ${data.monthlyOutflow.toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between gap-4 pt-1 border-t border-gray-100">
              <span className="text-gray-600">Net Flow:</span>
              <span className={`font-semibold ${data.netFlow >= 0 ? 'text-green-600' : 'text-red-600'}`}>
                ${data.netFlow.toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      );
    }
    return null;
  };

  return (
    <div className={`bg-white rounded-xl shadow-sm border border-gray-100 p-6 ${className}`}>
      <div className="mb-4">
        <h3 className="text-lg font-semibold text-gray-900 mb-2">Liquidity Analysis</h3>
        <p className="text-sm text-gray-600">
          Analysis of liquid assets and cash flow patterns. SBA requires minimum 3 months of expenses in liquid assets.
        </p>
      </div>
      
      <div className="h-80">
        <ResponsiveContainer width="100%" height="100%">
          <BarChart data={chartDataWithRequirement} margin={{ top: 20, right: 30, left: 20, bottom: 20 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
            <XAxis 
              dataKey="account" 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              angle={-45}
              textAnchor="end"
              height={80}
            />
            <YAxis 
              stroke="#6b7280"
              fontSize={12}
              tickLine={false}
              axisLine={false}
              tickFormatter={(value) => `$${(value / 1000).toFixed(0)}k`}
            />
            <Tooltip content={<CustomTooltip />} />
            
            <Bar 
              dataKey="currentBalance" 
              fill="#10b981" 
              name="Current Balance"
              radius={[2, 2, 0, 0]}
            />
            <Bar 
              dataKey="averageBalance" 
              fill="#3b82f6" 
              name="Average Balance"
              radius={[2, 2, 0, 0]}
            />
          </BarChart>
        </ResponsiveContainer>
      </div>
      
      {/* Liquidity Metrics */}
      <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">Liquidity Ratios</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900 text-sm">Current Liquidity Ratio</div>
                <div className="text-xs text-gray-600">Months of expenses covered</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  {currentLiquidityRatio.toFixed(1)}
                </div>
                <div className="text-xs text-gray-600">months</div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900 text-sm">Average Liquidity Ratio</div>
                <div className="text-xs text-gray-600">Based on average balances</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  {averageLiquidityRatio.toFixed(1)}
                </div>
                <div className="text-xs text-gray-600">months</div>
              </div>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          <h4 className="font-medium text-gray-900">SBA Requirements</h4>
          <div className="space-y-3">
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900 text-sm">Required Liquid Assets</div>
                <div className="text-xs text-gray-600">3 months of expenses</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  ${(sbaLiquidityRequirement / 1000).toFixed(0)}k
                </div>
              </div>
            </div>
            
            <div className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div>
                <div className="font-medium text-gray-900 text-sm">Current Liquid Assets</div>
                <div className="text-xs text-gray-600">Total bank balances</div>
              </div>
              <div className="text-right">
                <div className="text-lg font-semibold text-gray-900">
                  ${(totalLiquidAssets / 1000).toFixed(0)}k
                </div>
              </div>
            </div>
            
            <div className={`p-3 rounded-lg ${meetsSBARequirement ? 'bg-green-50' : 'bg-red-50'}`}>
              <div className="flex items-center gap-2">
                <div className={`w-2 h-2 rounded-full ${meetsSBARequirement ? 'bg-green-500' : 'bg-red-500'}`}></div>
                <div className={`text-sm font-medium ${meetsSBARequirement ? 'text-green-800' : 'text-red-800'}`}>
                  {meetsSBARequirement ? 'Meets SBA Requirement' : 'Below SBA Requirement'}
                </div>
              </div>
              <div className={`text-xs mt-1 ${meetsSBARequirement ? 'text-green-600' : 'text-red-600'}`}>
                {meetsSBARequirement 
                  ? `Exceeds requirement by $${((totalLiquidAssets - sbaLiquidityRequirement) / 1000).toFixed(0)}k`
                  : `Shortfall of $${((sbaLiquidityRequirement - totalLiquidAssets) / 1000).toFixed(0)}k`
                }
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

